import React, { useEffect, useState } from 'react';
import { Eye, Users, MousePointerClick, UserPlus, MessageSquareText, Heart, FileText } from 'lucide-react';
import { adminAnalytics } from '../../lib/api';

const PAGE_LABELS = {
  '/': 'Home', '/about': 'About', '/programs': 'Programs', '/community': 'Community',
  '/blog': 'Blog', '/contact': 'Contact', '/enroll': 'Enroll', '/profile': 'Profile',
};

function pageLabel(path) {
  if (PAGE_LABELS[path]) return PAGE_LABELS[path];
  if (path.startsWith('/blog/')) return `Article · ${path.replace('/blog/', '')}`;
  if (path.startsWith('/community/')) return `Discussion · ${path.replace('/community/', '').slice(0, 12)}`;
  return path;
}

function weekday(day) {
  try { return new Date(day + 'T00:00:00Z').toLocaleDateString(undefined, { weekday: 'short' }); }
  catch (e) { return day.slice(5); }
}

function Card({ label, value, icon: Icon, color }) {
  return (
    <div className="bg-white rounded-2xl p-5 shadow-[0_20px_50px_-42px_rgba(14,23,38,0.4)]">
      <span className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: `${color}1a`, color }}><Icon size={18} /></span>
      <div data-testid={`stat-${label}`} className="font-display font-extrabold text-[#0E1726] text-[26px] mt-3">{value}</div>
      <div className="text-sm text-[#5B6472]">{label}</div>
    </div>
  );
}

function BarChart({ series }) {
  const max = Math.max(1, ...series.map((s) => s.views));
  return (
    <div className="mt-5 flex items-end gap-1.5 h-40" data-testid="analytics-chart">
      {series.map((s) => (
        <div key={s.day} className="flex-1 flex flex-col items-center gap-2 group">
          <div className="w-full flex items-end justify-center h-full">
            <div className="w-full max-w-[26px] rounded-t-md bg-[#2E6BFF] group-hover:bg-[#1f5aef] transition-colors relative"
              style={{ height: `${(s.views / max) * 100}%`, minHeight: s.views ? '4px' : '0' }}
              title={`${s.views} views · ${s.visitors} visitors`}>
              <span className="absolute -top-5 left-1/2 -translate-x-1/2 text-[11px] font-semibold text-[#0E1726] opacity-0 group-hover:opacity-100 transition-opacity">{s.views}</span>
            </div>
          </div>
          <span className="text-[10px] text-[#5B6472]">{weekday(s.day)}</span>
        </div>
      ))}
    </div>
  );
}

function ListBars({ rows, color, valueKey, labelFn, unit }) {
  const max = Math.max(1, ...rows.map((r) => r[valueKey]));
  return (
    <div className="mt-4 space-y-3">
      {rows.map((r, i) => (
        <div key={i} className="flex items-center gap-3">
          <div className="w-56 shrink-0 text-sm text-[#0E1726] truncate" title={labelFn(r)}>{labelFn(r)}</div>
          <div className="flex-1 bg-[#F4F4F7] rounded-full h-3 overflow-hidden">
            <div className="h-full rounded-full" style={{ width: `${(r[valueKey] / max) * 100}%`, background: color }} />
          </div>
          <div className="w-24 text-right text-sm text-[#5B6472]"><span className="font-semibold text-[#0E1726]">{r[valueKey]}</span> {unit}</div>
        </div>
      ))}
    </div>
  );
}

export default function AnalyticsPanel() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    adminAnalytics(days).then(setData).finally(() => setLoading(false));
  }, [days]);

  if (loading && !data) return <div className="bg-white rounded-2xl p-10 text-center text-[#5B6472] shadow-[0_20px_50px_-44px_rgba(14,23,38,0.4)]">Loading analytics...</div>;
  if (!data) return null;

  const chart = data.views_series.slice(-14);
  const signupChart = data.signup_series.slice(-14);
  const signupMax = Math.max(1, ...signupChart.map((s) => s.members + s.subscribers));
  const noData = data.total_views === 0 && data.total_clicks === 0;

  const f = data.funnel;
  const stages = [
    { label: 'Visitors', value: f.visitors, color: '#2E6BFF' },
    { label: 'Sign-ups', value: f.signups, color: '#7C3AED' },
    { label: 'Paid enrollments', value: f.enrollments, color: '#10B981' },
  ];
  const funnelMax = Math.max(1, f.visitors, f.signups, f.enrollments);
  const conv = (a, b) => (b > 0 ? `${Math.min(100, Math.round((a / b) * 100))}%` : '—');

  return (
    <div data-testid="analytics-panel" className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-sm text-[#5B6472]">Human activity on your public pages (bots and admin pages excluded).</p>
        <div className="flex gap-2">
          {[7, 30, 90].map((d) => (
            <button key={d} data-testid={`range-${d}`} onClick={() => setDays(d)} className={`px-3 py-1.5 rounded-full text-sm font-semibold transition-colors ${days === d ? 'bg-[#0E1B2E] text-white' : 'bg-white text-[#0E1726] hover:bg-black/5'}`}>{d}d</button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card label={`Views (${data.days}d)`} value={data.total_views} icon={Eye} color="#2E6BFF" />
        <Card label={`Unique visitors`} value={data.unique_visitors} icon={Users} color="#0EA5E9" />
        <Card label={`CTA clicks`} value={data.total_clicks} icon={MousePointerClick} color="#F59E0B" />
        <Card label={`New sign-ups`} value={data.signups_members + data.signups_subscribers} icon={UserPlus} color="#7C3AED" />
      </div>

      {noData ? (
        <div className="bg-white rounded-2xl p-10 text-center shadow-[0_20px_50px_-44px_rgba(14,23,38,0.4)]">
          <Eye size={32} className="mx-auto text-[#2E6BFF]" />
          <h3 className="font-display font-bold text-[#0E1726] text-[18px] mt-3">No activity recorded yet</h3>
          <p className="text-[#5B6472] text-sm mt-1 max-w-md mx-auto">As people browse and interact with your public site, views, clicks, sign-ups and engagement will appear here.</p>
        </div>
      ) : (
        <>
          {/* Funnel */}
          <div className="bg-white rounded-2xl p-6 shadow-[0_20px_50px_-44px_rgba(14,23,38,0.4)]">
            <div className="text-[#0E1726] font-semibold">Conversion funnel · last {data.days} days</div>
            <div className="mt-4 space-y-3">
              {stages.map((s, i) => (
                <div key={s.label} className="flex items-center gap-3">
                  <div className="w-36 shrink-0 text-sm text-[#0E1726]">{s.label}</div>
                  <div className="flex-1 bg-[#F4F4F7] rounded-full h-6 overflow-hidden">
                    <div className="h-full rounded-full flex items-center justify-end px-2 text-white text-xs font-semibold" style={{ width: `${Math.max((s.value / funnelMax) * 100, 6)}%`, background: s.color }}>{s.value}</div>
                  </div>
                  <div className="w-24 text-right text-xs text-[#5B6472]">{i === 0 ? 'top of funnel' : `${conv(s.value, stages[i - 1].value)} of prev`}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Views trend */}
          <div className="bg-white rounded-2xl p-6 shadow-[0_20px_50px_-44px_rgba(14,23,38,0.4)]">
            <div className="text-[#0E1726] font-semibold">Views · last 14 days</div>
            <BarChart series={chart} />
          </div>

          {/* Sign-ups trend */}
          <div className="bg-white rounded-2xl p-6 shadow-[0_20px_50px_-44px_rgba(14,23,38,0.4)]">
            <div className="flex items-center justify-between">
              <div className="text-[#0E1726] font-semibold">Sign-ups · last 14 days</div>
              <div className="flex items-center gap-4 text-xs text-[#5B6472]">
                <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-[#7C3AED]" /> Members</span>
                <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-[#F6B93B]" /> Subscribers</span>
              </div>
            </div>
            <div className="mt-5 flex items-end gap-1.5 h-32">
              {signupChart.map((s) => (
                <div key={s.day} className="flex-1 flex flex-col items-center gap-2 group">
                  <div className="w-full flex flex-col justify-end items-center h-full" title={`${s.members} members · ${s.subscribers} subscribers`}>
                    <div className="w-full max-w-[24px] bg-[#F6B93B]" style={{ height: `${(s.subscribers / signupMax) * 100}%` }} />
                    <div className="w-full max-w-[24px] bg-[#7C3AED] rounded-t-md" style={{ height: `${(s.members / signupMax) * 100}%` }} />
                  </div>
                  <span className="text-[10px] text-[#5B6472]">{weekday(s.day)}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            {/* Top CTAs */}
            <div className="bg-white rounded-2xl p-6 shadow-[0_20px_50px_-44px_rgba(14,23,38,0.4)]">
              <div className="text-[#0E1726] font-semibold flex items-center gap-2"><MousePointerClick size={17} className="text-[#F59E0B]" /> Top button clicks</div>
              {data.top_ctas.length ? (
                <ListBars rows={data.top_ctas} color="#F59E0B" valueKey="clicks" unit="clicks" labelFn={(r) => r.label} />
              ) : <p className="text-sm text-[#5B6472] mt-3">No button clicks tracked yet.</p>}
            </div>
            {/* Top pages */}
            <div className="bg-white rounded-2xl p-6 shadow-[0_20px_50px_-44px_rgba(14,23,38,0.4)]">
              <div className="text-[#0E1726] font-semibold flex items-center gap-2"><Eye size={17} className="text-[#2E6BFF]" /> Top pages</div>
              {data.top_pages.length ? (
                <ListBars rows={data.top_pages} color="#7C3AED" valueKey="views" unit="views" labelFn={(r) => pageLabel(r.path)} />
              ) : <p className="text-sm text-[#5B6472] mt-3">No page views tracked yet.</p>}
            </div>
          </div>

          {/* Community engagement */}
          <div className="bg-white rounded-2xl p-6 shadow-[0_20px_50px_-44px_rgba(14,23,38,0.4)]">
            <div className="text-[#0E1726] font-semibold">Community engagement (all-time)</div>
            <div className="grid grid-cols-3 gap-4 mt-4">
              {[
                { label: 'Discussions', value: data.engagement.posts, icon: FileText, color: '#2E6BFF' },
                { label: 'Replies', value: data.engagement.replies, icon: MessageSquareText, color: '#10B981' },
                { label: 'Likes', value: data.engagement.likes, icon: Heart, color: '#EF4444' },
              ].map((e) => (
                <div key={e.label} className="text-center">
                  <e.icon size={20} className="mx-auto" style={{ color: e.color }} />
                  <div className="font-display font-extrabold text-[#0E1726] text-[24px] mt-2">{e.value}</div>
                  <div className="text-sm text-[#5B6472]">{e.label}</div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
